import type { ReactNode } from 'react'

import { AccentScope } from '@/components/accent'
import { Button } from '@/components/button'
import { FormError } from '@/components/form-error'
import { Card, Page } from '@/components/layout'
import { TopBar, type TopBarProps } from '@/features/nav/top-bar'
import { claim } from '@/lib/api'
import { cx } from '@/lib/cx'
import { useT } from '@/lib/i18n/translate'

/** The customer pages' header (T13): the same bar as the app, as the page's own heading. */
export function CustomerHeader(props: Omit<TopBarProps<'restaurant'>, 'system' | 'customer' | 'page'>) {
  return <TopBar system="restaurant" customer {...props} />
}

interface CustomerShellProps {
  color: string | null | undefined
  /** The Romi credit, or the copyright on Romi's own page. */
  footer?: 'powered' | 'copyright'
  /** Room under the footer for a fixed bottom bar. */
  bottomBar?: boolean
  children: ReactNode
}

/** Accent + full-height column around a customer page, ending with the footer so no page can forget it. */
export function CustomerShell({ color, footer = 'powered', bottomBar = false, children }: CustomerShellProps) {
  return (
    <AccentScope color={color}>
      <div className="flex min-h-dvh flex-col">
        {children}
        <CustomerFooter variant={footer} bottomBar={bottomBar} />
      </div>
    </AccentScope>
  )
}

const DOMAIN = 'romi.io.vn'

/** The domain is plain text, never a link: it must not pull a diner out of the flow. */
function CustomerFooter({ variant, bottomBar }: { variant: 'powered' | 'copyright'; bottomBar: boolean }) {
  const t = useT()
  return (
    <footer className={cx('text-muted mt-auto px-4 py-4 text-center text-sm', bottomBar && 'pb-[calc(4rem+max(1rem,env(safe-area-inset-bottom)))]')}>
      {variant === 'powered' ? `${t('landing.poweredBy')} · ${DOMAIN}` : t('landing.footer', { year: new Date().getFullYear() })}
    </footer>
  )
}

export function CustomerLoading() {
  const t = useT()
  return (
    <Page width="sm">
      <p className="text-muted text-sm">{t('common.loading')}</p>
    </Page>
  )
}

/** A dead link (404 of any kind) says so; anything else can be retried. */
export function CustomerLoadError({ error, notFound, retry }: { error: unknown; notFound: string; retry: () => void }) {
  const t = useT()
  const missing = claim(error, 'NOT_FOUND', 'TABLE_NOT_FOUND')
  return (
    <Page width="sm" className="pt-10">
      <Card className="flex flex-col gap-4 p-6">
        {missing ? (
          <p role="alert">{missing.code === 'TABLE_NOT_FOUND' ? t('error.TABLE_NOT_FOUND') : notFound}</p>
        ) : (
          <>
            <FormError error={error} />
            <Button variant="secondary" onClick={retry} className="self-start">
              {t('common.retry')}
            </Button>
          </>
        )}
      </Card>
    </Page>
  )
}
